import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Phone, EnvelopeSimple, FloppyDisk, PaperPlaneRight, UsersThree } from "@phosphor-icons/react";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [saving, setSaving] = useState(false);
  // local string buffers (comma-separated) for multi-recipient inputs
  const [phonesText, setPhonesText] = useState("");
  const [emailsText, setEmailsText] = useState("");
  const [daysText, setDaysText] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setPhonesText((r.data.alert_phones || []).join(", "));
      setEmailsText((r.data.alert_emails || []).join(", "));
      setDaysText((r.data.reminder_days || []).join(","));
    });
    api.get("/reminders/schedule").then((r) => setSchedule(r.data)).catch(() => {});
  }, []);

  const setField = (k, v) => setSettings({ ...settings, [k]: v });

  const parseList = (s, sep = ",") => s.split(sep).map((x) => x.trim()).filter(Boolean);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        alert_phones: parseList(phonesText),
        alert_emails: parseList(emailsText),
        sms_enabled: !!settings.sms_enabled,
        email_enabled: !!settings.email_enabled,
        notify_practitioner: !!settings.notify_practitioner,
        reminder_days: parseList(daysText).map((x) => parseInt(x, 10)).filter((n) => !isNaN(n) && n > 0),
      };
      const r = await api.put("/settings", payload);
      setSettings(r.data);
      setPhonesText((r.data.alert_phones || []).join(", "));
      setEmailsText((r.data.alert_emails || []).join(", "));
      setDaysText((r.data.reminder_days || []).join(","));
      toast.success("Settings saved");
    } catch (err) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const testSms = async () => {
    try {
      const r = await api.post("/reminders/test-sms");
      const sent = (r.data.results || []).filter((x) => x.status === "sent");
      const skipped = (r.data.results || []).filter((x) => x.status !== "sent");
      if (sent.length) toast.success(`SMS sent to ${sent.length} recipient(s)`);
      if (skipped.length) toast.warning(`${skipped.length} skipped — ${skipped[0].reason || skipped[0].error || ""}`);
    } catch { toast.error("Test SMS failed"); }
  };

  const testEmail = async () => {
    try {
      const r = await api.post("/reminders/test-email");
      const sent = (r.data.results || []).filter((x) => x.status === "sent");
      const skipped = (r.data.results || []).filter((x) => x.status !== "sent");
      if (sent.length) toast.success(`Email sent to ${sent.length} recipient(s)`);
      if (skipped.length) toast.warning(`${skipped.length} skipped — ${skipped[0].reason || skipped[0].error || ""}`);
    } catch { toast.error("Test email failed"); }
  };

  if (!settings) return <div className="text-gray-500">Loading…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <header className="border-b border-gray-200 pb-5">
        <div className="overline text-gray-500">Notification configuration</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 mt-1">Settings</h1>
        <p className="text-sm text-gray-600 mt-2">
          Route expiration alerts to one or many on-call coordinators, and optionally to each practitioner's own phone &amp; email.
        </p>
      </header>

      <section className="bg-white border border-gray-200 p-6 space-y-5">
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">
            Coordinator alert phones (comma-separated, E.164)
          </label>
          <div className="mt-1 flex items-start gap-2">
            <Phone size={14} className="text-gray-500 mt-2.5" />
            <textarea
              value={phonesText}
              onChange={(e) => setPhonesText(e.target.value)}
              placeholder="+15045551234, +15045555678"
              rows={2}
              className="flex-1 border border-gray-300 bg-white px-3 py-2 text-sm font-mono"
              data-testid="settings-alert-phones"
            />
          </div>
          <div className="text-[11px] text-gray-500 mt-1">Every phone here receives every reminder. E.g., on-call + backup credentialing manager.</div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">
            Coordinator alert emails (comma-separated)
          </label>
          <div className="mt-1 flex items-start gap-2">
            <EnvelopeSimple size={14} className="text-gray-500 mt-2.5" />
            <textarea
              value={emailsText}
              onChange={(e) => setEmailsText(e.target.value)}
              placeholder="coordinator@clinic.org, manager@clinic.org"
              rows={2}
              className="flex-1 border border-gray-300 bg-white px-3 py-2 text-sm"
              data-testid="settings-alert-emails"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="flex items-center gap-2 text-sm border border-gray-200 px-3 py-2 bg-gray-50">
            <input type="checkbox" checked={!!settings.sms_enabled} onChange={(e) => setField("sms_enabled", e.target.checked)} data-testid="settings-sms-enabled" />
            <span>SMS reminders (Twilio)</span>
          </label>
          <label className="flex items-center gap-2 text-sm border border-gray-200 px-3 py-2 bg-gray-50">
            <input type="checkbox" checked={!!settings.email_enabled} onChange={(e) => setField("email_enabled", e.target.checked)} data-testid="settings-email-enabled" />
            <span>Email reminders (Resend)</span>
          </label>
          <label className="flex items-center gap-2 text-sm border border-gray-200 px-3 py-2 bg-gray-50" title="Also notify each practitioner using their own phone & email">
            <input type="checkbox" checked={!!settings.notify_practitioner} onChange={(e) => setField("notify_practitioner", e.target.checked)} data-testid="settings-notify-practitioner" />
            <UsersThree size={14} weight="bold" />
            <span>Notify practitioners directly</span>
          </label>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Reminder thresholds (days before expiration)</label>
          <input
            value={daysText}
            onChange={(e) => setDaysText(e.target.value)}
            placeholder="7,30,60"
            className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm font-mono"
            data-testid="settings-reminder-days"
          />
          <div className="text-xs text-gray-500 mt-1">Comma-separated integers. Reminders fire when days-to-expiry is at or near these values. Expired items always trigger.</div>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-200">
          <button onClick={save} disabled={saving} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 inline-flex items-center gap-2 disabled:opacity-60" data-testid="settings-save-btn">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save settings"}
          </button>
          <button onClick={testSms} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100 inline-flex items-center gap-2" data-testid="test-sms-btn">
            <PaperPlaneRight size={14} /> Send test SMS
          </button>
          <button onClick={testEmail} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100 inline-flex items-center gap-2" data-testid="test-email-btn">
            <PaperPlaneRight size={14} /> Send test email
          </button>
        </div>
      </section>

      <section className="bg-white border border-gray-200 p-6">
        <div className="overline text-gray-500">Automation</div>
        <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5 mb-4">Scheduled reminder job</h2>
        {schedule ? (
          <div className="text-sm text-gray-700 space-y-2">
            <div>• Cadence: <span className="font-mono font-semibold text-slate-900">{schedule.cadence}</span></div>
            <div>• Timezone: <span className="font-mono">{schedule.timezone}</span></div>
            <div>• Running: <span className="font-mono">{String(schedule.running)}</span></div>
            {schedule.jobs?.[0]?.next_run_time && (
              <div>• Next run: <span className="font-mono">{new Date(schedule.jobs[0].next_run_time).toLocaleString()}</span></div>
            )}
          </div>
        ) : <div className="text-sm text-gray-500">Loading schedule…</div>}
      </section>

      <section className="bg-white border border-gray-200 p-6">
        <div className="overline text-gray-500">System</div>
        <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5 mb-4">Integration status</h2>
        <ul className="text-sm text-gray-700 space-y-2">
          <li>• Twilio SMS: <span className="font-mono">{settings.sms_enabled ? "ENABLED" : "DISABLED"}</span></li>
          <li>• Resend Email: <span className="font-mono">{settings.email_enabled ? "ENABLED" : "DISABLED"}</span> {!settings.email_enabled && <span className="text-xs text-gray-500 ml-1">(set RESEND_API_KEY in backend .env to enable)</span>}</li>
          <li>• GPT-5.2 Vision OCR: <span className="font-mono">ENABLED (Emergent LLM Key)</span></li>
          <li>• Emergent Object Storage: <span className="font-mono">ENABLED</span></li>
          <li>• Notify practitioners directly: <span className="font-mono">{settings.notify_practitioner ? "YES" : "NO"}</span></li>
        </ul>
      </section>
    </div>
  );
}
